import is from "is";
import { getFileUrlFromDriveInfo, checkGraphResponse } from "../GraphMain";
import { getWorksheetUrlSection } from "../helper";
import { getIdFromNormalizedElementTypeId, jsonCopy, isJsonString, getTargetElementDirection } from "../../utils/helper";
import { getLocalCellNameFromAddress, getSheetNameFromAddressForUrl, getRangeStartOffset, getExcelCoords } from "../../utils/helperExcel";

export async function writeLiveChanges(
  elementInfo: any,
  sessionId: string,
  changesList: any[],
  changesListPrior: any[],
  client
) {
  let changedValues = [];

  try {
    let worksheetId = elementInfo.basics.worksheetId;
    if (!worksheetId) worksheetId = elementInfo.basics.worksheetName;
    const address = getLocalCellNameFromAddress(elementInfo.range.address);
    const fileUrl = getFileUrlFromDriveInfo(elementInfo.accessInfo.driveItemInfo);
    const rangeUrl = fileUrl+"/workbook/"+getWorksheetUrlSection(worksheetId)+"/range(address='"+address+"')?$select=values";
    let requests = [];

    for (const change of changesList) {
      const changeWorksheetId = getIdFromNormalizedElementTypeId(change.worksheetId);
      const changeCellAddress = getLocalCellNameFromAddress(change.address);
      const cellChangeUrl = fileUrl + "/workbook/"+getWorksheetUrlSection(changeWorksheetId)+"/range(address='"+changeCellAddress+"')";
      const newValue = change.newValue;
      const cellChangeWorkbookRange = {"values" : [[newValue]]};

      await client
        .api(cellChangeUrl)
        .header("workbook-session-id", sessionId)
        .update(cellChangeWorkbookRange)
    }

    // build the result request
    requests.push({
      url: rangeUrl,
      method: "GET",
      id: "RANGE",
      // dependsOn: ["CHANGE"],
      headers: {
        "workbook-session-id": sessionId
      }
    })
    // add the external ranges (for charts & dropdowns)



    const $batch = {
      requests: requests,
    };
    // RUN BATCH
    let res = await client
      .api("/$batch")
      .post($batch);
    checkGraphResponse(res, "writeLiveChanges");

    // COMPARE RESULTS
    let newRange = res.responses[res.responses.findIndex((x) => x.id === "RANGE")]?.body?.values;
    if (newRange) {
      const oldRange = elementInfo.range.values;
      const oldFormulas = elementInfo.range.formulas;
      // look for formulas
      const rowCount = oldFormulas.length;
      for (let row = 0, len = rowCount; row < len; ++row) {
        const values = oldFormulas[row];
        if (values) {

          var el = values.find(a => String(a).includes("="));
          if (el?.length === 0) continue;
          // found a formula in that row
          const colCount = values.length;
          for (let col = 0, len = colCount; col < len; ++col) {
            if (!is.string(values[col])) continue;
            if (values[col].includes("=")) {
              // found a formula col/row cell
              const oldValue = oldRange[row][col];
              const newValue = newRange[row][col];
              // is there a change
              // let needsToBeUpdated = changesListPrior.findIndex(
              //   (x) => x.row === row && x.col === col
              // ) > -1;

              if (oldValue !== newValue) {
                changedValues.push({
                  row: row,
                  col: col,
                  oldValue: oldValue,
                  newValue: newValue
                })
              }
            }
          }
        }

      }
    }
  } catch (error) {
    console.log("ERROR writeLiveChanges", error);
  }

  return changedValues;
}

export async function writeLiveCellArray(
  elementInfo: any,
  sessionId: string,
  client,
  array: any[],
  targetCell: string,
  fields: any[],
) {
  try {
    const worksheetId = getSheetNameFromAddressForUrl(targetCell);
    const fileUrl = getFileUrlFromDriveInfo(elementInfo.accessInfo.driveItemInfo);
    const startAddress = getLocalCellNameFromAddress(targetCell);
    const startOffset = getRangeStartOffset(targetCell)
    
    const arrayColumnCount = array[0].length;
    const arrayRowCount = array.length;
    const endAddress = getExcelCoords(startOffset.row+arrayRowCount+1, startOffset.col+arrayColumnCount+1);
    const arrayAddress = startAddress+":"+endAddress;
    const areaChangeUrl = fileUrl + "/workbook/"+getWorksheetUrlSection(worksheetId)+"/range(address='"+arrayAddress+"')";
    const arrayChangeWorkbookRange = {"values" : array};
    // write the array
    await client
      .api(areaChangeUrl)
      .header("workbook-session-id", sessionId)
      .update(arrayChangeWorkbookRange)
  } catch (error) {
    console.log("ERROR writeLiveCellArray", error);
  }
}

//================================
// CALCULATIONS
//================================

export async function getLiveTargetValues(
  elementInfo: any,
  sessionId: string,
  targetList: any[],
  direction: string,
  client: any)
{
  const fileUrl = getFileUrlFromDriveInfo(elementInfo.accessInfo.driveItemInfo);
  let requests = [];
  let results = [];
  // define a batch request for all targets
  for (const target of targetList) {
    let isOK = (direction) ? target.direction === direction : true;
    if (["Checkbox", "DatePicker", "TimePicker"].includes(target.element)) {
      isOK = true;
    }
    if (target.staticData === true && (target.value)) {
      isOK = false;
    }
    // address
    if (isOK === true) {
      try {
        if (target.element === "ResultChart") {
          if (target.address) {
            // get the chart detail
            let worksheetId;
            let chartId;
            if (is.string(target.address)) {
              if (isJsonString(target.address)) {
                const chartJson = JSON.parse(target.address) ?? {};
                worksheetId = chartJson.worksheetId;
                chartId = chartJson.chartId;
              } else if (target.address.includes("!")) {
                worksheetId = getSheetNameFromAddressForUrl(target.address);
                chartId = target.address.replace(worksheetId+"!", "");
              }
            } else {
              const chartJson = target.address;
              worksheetId = chartJson.worksheetId;
              chartId = chartJson.chartId;
            }

            const rangeUrl = fileUrl+"/workbook/"+getWorksheetUrlSection(worksheetId) + "/charts/"+chartId+"/image(width=2000)";
            let request = {
              url: rangeUrl,
              method: "GET",
              id: target.field_name,
              headers: (sessionId) ? {
                "workbook-session-id": sessionId,
                "Content-Type": "application/json"
              } : {}
            };
            requests.push(request);
          }
        } else {
          const cellAddress = getLocalCellNameFromAddress(target.address).replaceAll("$", "");
          const cellWorksheet = getSheetNameFromAddressForUrl(target.address);
          if (cellWorksheet) {
            const rangeUrl = fileUrl+"/workbook/" + getWorksheetUrlSection(cellWorksheet) + "/range(address='"+cellAddress+"')?$select=values,text";
            let request = {
              url: rangeUrl,
              method: "GET",
              id: target.field_name,
              headers: (sessionId) ? {
                "workbook-session-id": sessionId,
                "Content-Type": "application/json"
              } : {}
            };
            requests.push(request);
          }
        }
      }
      catch (error) {
        console.log("ERROR getLiveTargetValues", error);
      }
    }
  }
  // RUN BATCH
  if (requests.length > 0) {
    // max 18 entries in batch
    let batchList = [];
    let batchSubList = [];
    for (const request of requests) {
      // create a new sublist and store it in main
      if (batchSubList.length === 18) {
        batchList.push(jsonCopy(batchSubList));
        batchSubList.length = 0;
      }
      batchSubList.push(request);
    }
    if (batchSubList.length > 0) {
      batchList.push(jsonCopy(batchSubList));
    }

    let res = [];
    for (const batchRequests of batchList) {
      const $batch = {
        requests: batchRequests,
      };
      let resSub = await client
        .api("/$batch")
        .post($batch);

      checkGraphResponse(res, "getLiveTargetValues");
      // add the responses to the main res
      if (resSub) {
        const responses = resSub["responses"];
        for (const response of responses) {
          res.push(jsonCopy(response));
        }
      }
    }

    if (res.length > 0) {
      for (const target of targetList) {
        try {
          const resElement = res[res.findIndex((x) => x.id === target.field_name)]?.body;
          if (resElement) {
            let newValue;
            let newValueText;
            if (target.element === "ResultChart") {
              newValue = (resElement.value) ? resElement.value : null;
              newValueText = "bitmap";
            } else if (target.element === "ResultTable") {
              newValue = resElement.values;
              newValueText = resElement.text;
            } else if (target.element === "SpreadTable") {
              newValue = resElement.values;
              newValueText = resElement.text;
            } else if (target.element === "TableView") {
              newValue = resElement.values;
              newValueText = resElement.text;
            } else if (target.element === "Variables") {
              newValue = resElement.values;
              newValueText = resElement.text;
            } else {
              newValue = (resElement.values) ? resElement.values[0][0] : null;
              newValueText = (resElement.text) ? resElement.text[0][0] : null;
            }

            if (target.value !== newValueText) {
              const targetResult = {
                field_name: target.field_name,
                address: target.address,
                valueText: newValueText,
                value: newValue,
                direction: getTargetElementDirection(target),
                element: target.element
              }
              if (target.variableContent) targetResult["variableContent"] = target.variableContent;
              results.push(targetResult);
            }
          // } else if (target.element === "Variable" && target.address) {
          //   const targetResult = {
          //     field_name: target.field_name,
          //     address: target.address,
          //     value: target.value,
          //     title: target.title,
          //     element: target.element
          //   }
          //   results.push(targetResult);
          }
        } catch (err) {
          console.log("ERROR getLiveTargetValues", err);
        }
      }
    }
  }

  return results;
}

export async function setLiveTargetValues(
  elementInfo: any,
  sessionId: string,
  targetList: any[],
  client: any)
{
  let isError = false;
  const fileUrl = getFileUrlFromDriveInfo(elementInfo.accessInfo.driveItemInfo);
  // let requests = [];
  // define a batch request for all targets
  for (const target of targetList) {
    const changedValue = (target.changed === true || target.changed === undefined || target.changed === null);
    if (target.direction === "in" && changedValue) {
      // add all variables
      const cellAddress = getLocalCellNameFromAddress(target.address).replaceAll("$", "");
      const cellWorksheet = getSheetNameFromAddressForUrl(target.address);
      if (cellWorksheet) {
        const rangeUrl = fileUrl+"/workbook/" + getWorksheetUrlSection(cellWorksheet) + "/range(address='"+cellAddress+"')";
        const newValue = target.value;
        const cellChangeWorkbookRange = {
          values: [[newValue]],
        };
        try {
          await client
          .api(rangeUrl)
          .header("workbook-session-id", sessionId)
          .update(cellChangeWorkbookRange)
          console.log("INFO changed cell: " + cellAddress + ", " + target.value)
        } catch (err) {
          isError = true;
          console.log("ERROR updating cell: " + target.address)
        }
      }

      // let request = {
      //   url: rangeUrl,
      //   method: "UPDATE",
      //   id: target.field_name,
      //   // dependsOn: ["CHANGE"],
      //   headers: (sessionId) ? {
      //     "workbook-session-id": sessionId,
      //     "Content-Type": "application/json"
      //   } : {},
      //   body: { values: [[newValue]] }
      // };
      // requests.push(request);

      // RUN BATCH
      // if (requests.length > 0) {
      //   const $batch = {
      //   requests: requests,
      //   };
      //   let res = await client
      //     .api("/$batch")
      //     .post($batch);
      // }
    }
  } 
  return isError;
}

export async function getLiveTargetValuesForChanges(
  elementInfo: any,
  sessionId: string,
  targetList: any[],
  client: any)
{
  const isError = await setLiveTargetValues(elementInfo, sessionId, targetList, client);
  console.log("INFO setLiveTargetValues isError: " + isError);

  const results = await getLiveTargetValues(elementInfo, sessionId, targetList, "out", client);
  return results;
}

export async function getLiveTargetLookups(
  elementInfo: any,
  sessionId: string,
  targetList: any[],
  client: any)
{
  const fileUrl = getFileUrlFromDriveInfo(elementInfo.accessInfo.driveItemInfo);
  let requests = [];
  let results = [];
  // define a batch request for all targets
  for (const target of targetList) {
    const isOK = (target.source_address && target.source_address.length > 0);
    // address
    if (isOK === true) {
      const cellAddress = getLocalCellNameFromAddress(target.source_address).replaceAll("$", "");
      const cellWorksheet = getSheetNameFromAddressForUrl(target.source_address);
      if (cellWorksheet && cellAddress) {
        const rangeUrl = fileUrl+"/workbook/" + getWorksheetUrlSection(cellWorksheet) + "/range(address='"+cellAddress+"')?$select=values,text";
        let request = {
          url: rangeUrl,
          method: "GET",
          id: target.field_name,
          headers: (sessionId) ? {
            "workbook-session-id": sessionId,
            "Content-Type": "application/json"
          } : {}
        };
        requests.push(request);
      }
    }
  }

  // RUN BATCH
  if (requests.length > 0) {
    // max 18 entries in batch
    let batchList = [];
    let batchSubList = [];
    for (const request of requests) {
      // create a new sublist and store it in main
      if (batchSubList.length === 18) {
        batchList.push(jsonCopy(batchSubList));
        batchSubList.length = 0;
      }
      batchSubList.push(request);
    }
    if (batchSubList.length > 0) {
      batchList.push(jsonCopy(batchSubList));
    }

    let res = [];
    for (const batchRequests of batchList) {
      const $batch = {
        requests: batchRequests,
      };
      let resSub = await client
        .api("/$batch")
        .post($batch);

      checkGraphResponse(res, "getLiveTargetValues");
      // add the responses to the main res
      if (resSub) {
        const responses = resSub["responses"];
        for (const response of responses) {
          res.push(jsonCopy(response));
        }
      }
    }

    if (res.length > 0) {
      for (const target of targetList) {
        try {
          const resElement = res[res.findIndex((x) => x.id === target.field_name)]?.body;
          if (resElement.values && resElement.values.length > 0) {
            let elementValues = [];
            let elementInstructions = [];
            // get the single values
            resElement.values.forEach((item) => {
              const value = item[0]; //.replaceAll("\n", "").trim();
              const instruction = item[1];
              if (value || value === 0) {
                elementValues.push(value);
                elementInstructions.push(instruction);
              }
            });
            // store in the main list
            results.push({
              field_name: target.field_name,
              lookupValues: elementValues,
              lookupInstructions: elementInstructions
            });
          }
        } catch (err) {
          console.log("ERROR getLiveTargetLookups", err);
        }
      }
    }
  }

  return results;
}

export async function getLiveRangeValues(
  elementInfo: any,
  sessionId: string,
  rangesList: any[],
  client: any)
{
  const fileUrl = getFileUrlFromDriveInfo(elementInfo.accessInfo.driveItemInfo);
  let requests = [];

  // define a batch request for all targets
  for (const target of rangesList) {
    const isOK = (target.range && target.range.length > 0);
    // address
    if (isOK === true) {
      const cellAddress = getLocalCellNameFromAddress(target.range).replaceAll("$", "");
      const cellWorksheet = getSheetNameFromAddressForUrl(target.range);
      if (cellWorksheet && cellAddress) {
        const rangeUrl = fileUrl+"/workbook/" + getWorksheetUrlSection(cellWorksheet) + "/range(address='"+cellAddress+"')?$select=values,text";
        let request = {
          url: rangeUrl,
          method: "GET",
          id: target.range,
          headers: (sessionId) ? {
            "workbook-session-id": sessionId,
            "Content-Type": "application/json"
          } : {}
        };
        requests.push(request);
      }
    }
  }

  // RUN BATCH
  if (requests.length > 0) {
    // max 18 entries in batch
    let batchList = [];
    let batchSubList = [];
    for (const request of requests) {
      // create a new sublist and store it in main
      if (batchSubList.length === 18) {
        batchList.push(jsonCopy(batchSubList));
        batchSubList.length = 0;
      }
      batchSubList.push(request);
    }
    if (batchSubList.length > 0) {
      batchList.push(jsonCopy(batchSubList));
    }

    let res = [];
    for (const batchRequests of batchList) {
      const $batch = {
        requests: batchRequests,
      };
      let resSub = await client
        .api("/$batch")
        .post($batch);

      checkGraphResponse(res, "getLiveRangeValues");
      // add the responses to the main res
      if (resSub) {
        const responses = resSub["responses"];
        for (const response of responses) {
          res.push(jsonCopy(response));
        }
      }
    }

    if (res.length > 0) {
      for (const target of rangesList) {
        try {
          const resElement = res[res.findIndex((x) => x.id === target.range)]?.body;
          if (resElement.values && resElement.values.length > 0) {
            // let elementValues = [];
            // let elementInstructions = [];

            target.values = resElement.values;
            // get the single values
            // resElement.values.forEach((item) => {
            //   const value = item[0];
            //   const instruction = item[1];
            //   if (value || value === 0) {
            //     elementValues.push(value);
            //     elementInstructions.push(instruction);
            //   }
            // });
            // store in the main list
            // results.push({
            //   field_name: target.field_name,
            //   lookupValues: elementValues,
            //   lookupInstructions: elementInstructions
            // });
          }
        } catch (err) {
          console.log("ERROR getLiveRangeValues 2", err);
        }
      }
    }
  }
  return rangesList;
}

export async function getLiveCellValue(
  elementInfo: any,
  sessionId: string,
  address: string,
  client: any)
{  if (!address) return null;
  const fileUrl = getFileUrlFromDriveInfo(elementInfo.accessInfo.driveItemInfo);
  let requests = [];
  let addressValue;

  // define a batch request for all targets
  const cellAddress = getLocalCellNameFromAddress(address).replaceAll("$", "");
  const cellWorksheet = getSheetNameFromAddressForUrl(address);
  if (cellWorksheet && cellAddress) {
    const rangeUrl = fileUrl+"/workbook/" + getWorksheetUrlSection(cellWorksheet) + "/range(address='"+cellAddress+"')?$select=values,text";
    let request = {
      url: rangeUrl,
      method: "GET",
      id: address,
      headers: (sessionId) ? {
        "workbook-session-id": sessionId,
        "Content-Type": "application/json"
      } : {}
    };
    requests.push(request);
  }

  // RUN BATCH
  if (requests.length > 0) {
    // max 18 entries in batch
    let batchList = [];
    let batchSubList = [];
    for (const request of requests) {
      // create a new sublist and store it in main
      if (batchSubList.length === 18) {
        batchList.push(jsonCopy(batchSubList));
        batchSubList.length = 0;
      }
      batchSubList.push(request);
    }
    if (batchSubList.length > 0) {
      batchList.push(jsonCopy(batchSubList));
    }

    let res = [];
    for (const batchRequests of batchList) {
      const $batch = {
        requests: batchRequests,
      };
      let resSub = await client
        .api("/$batch")
        .post($batch);

      checkGraphResponse(res, "getLiveCellValue");
      // add the responses to the main res
      if (resSub) {
        const responses = resSub["responses"];
        for (const response of responses) {
          res.push(jsonCopy(response));
        }
      }
    }

    if (res.length > 0) {
      try {
        const resElement = res[res.findIndex((x) => x.id === address)]?.body;
        if (resElement.values && resElement.values.length > 0) {
          addressValue = resElement.values;
          // get the single values
          resElement.values.forEach((item) => {
            const value = item[0];
            if (value || value === 0) {
              addressValue = value;
            }
          });
        }
      } catch (err) {
        console.log("ERROR getLiveCellValues 2", err);
      }
    }
  }
  return addressValue;
}

export async function sendSessionPing(
  elementInfo: any,
  sessionId: string,
  client: any,
  errorCallback: any)
{
  console.log("INFO send calculation session ping")
  const fileUrl = getFileUrlFromDriveInfo(elementInfo.accessInfo.driveItemInfo);
  // simmply get the worksheets to keep the session alive
  await client
    .api(fileUrl + "/workbook/worksheets")
    .header("workbook-session-id", sessionId)
    .get()
    .catch(error => {
      // console.error('Error sendSessionPing graph', error);
      if (errorCallback) errorCallback(error);
    });

  // const workbookSessionInfo = {};
  // let sessionRefresh = await client
  //   .api(fileUrl + "/workbook/refreshSession")
  //   .header("workbook-session-id", sessionId)
  //   .post(workbookSessionInfo)
  //   .then((data) => {
  //     console.log("LIVESESSION refreshed", data, sessionId);
  //     return data;
  //     // update the timestamp
  //     // foundSession.timeStamp = new Date();
  //     // return foundSession;
  //   })
  //   .catch((err) => {
  //     console.log("ERROR refreshing LiveSession", err);
  //     return null;
  //   });
}
